import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { DebugableService } from '../debug/debug.type';
import { AuthService } from '../auth/auth.service';
import {
  ClientAuthEvent,
  ClientHeartbeatEvent,
  ClientHelloEvent,
  ClientSubscribeEvent,
  ClientUnsubscribeEvent,
  FluidPrefixType,
  RealtimeEventType,
  SystemAckEvent,
  SystemErrorEvent,
  SystemHelloEvent,
  REALTIME_EVENT_MAP,
} from '@flux/core/client';
import type * as pkg from '@flux/core/client';
import { debounceTime, Subject } from 'rxjs';
import { environment } from '@/environments/environment';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { FeatureFlagsEnum } from '@/generated/featureFlags.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { safeJsonParse } from '@core/utils/safe-json-parse';
import { fluxLogger } from '../logger/logger.instance';

const logger = fluxLogger.extend('socket');
const eventLogger = logger.extend('event');

type core = typeof pkg;

type ClassType<T> = T extends abstract new (...args: any) => infer R ? R : never;

export type RealtimeEventMap = {
  [key in RealtimeEventType]: core extends Record<`${key}Event`, infer T> ? ClassType<T> : never;
};

export interface RealtimeEventPayload<O extends RealtimeEventType> {
  op: O;
  data: RealtimeEventMap[O];
}

@Injectable({ providedIn: 'root' })
export class RealtimeService implements DebugableService, OnDestroy {
  public static isEventFromType<O extends RealtimeEventType>(
    event: RealtimeEventPayload<any>,
    type: O,
  ): event is RealtimeEventPayload<O> {
    return event.op === type;
  }

  public static isOneOfEventTypes<O extends RealtimeEventType>(
    event: RealtimeEventPayload<any>,
    types: O[],
  ): event is RealtimeEventPayload<O> {
    return types.includes(event.op as O);
  }

  private ws: WebSocket | null = null;
  private interval: any = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  private retryAttempts = 0;
  private maxRetryAttempts = 10;

  private isNetworkOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private boundOnlineHandler = this.handleOnline.bind(this);
  private boundOfflineHandler = this.handleOffline.bind(this);
  private boundVisibilityHandler = this.handleVisibilityChange.bind(this);

  private debugMessages: unknown[] = [];
  private debugConnectionDetails: {
    connectedAt: Date | null;
    disconnectedAt: Date | null;
    firstMessageAt: Date | null;
    lastMessageAt: Date | null;
  }[] = [];

  private helloId: string | null = null;

  public subscriptions = new Set<string>();

  private readonly subscribeEvents: Record<
    string,
    { id: string; type: 'add' | 'remove'; fluids: FluidPrefixType[] }
  > = {};

  public readonly eventReceived$ = new Subject<RealtimeEventPayload<RealtimeEventType>>();

  private lastConnectedToken: string | null = null;

  private connectionState: 'connecting' | 'connected' | 'disconnected' = 'disconnected';

  private queue: ((instance: RealtimeService) => void | Promise<void>)[] = [];

  public constructor(
    private readonly authService: AuthService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.initializeAuthSubscription();
    this.initializeNetworkListeners();
    this.initializeVisibilityListener();
    this.initializeConnection();
  }

  public ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.removeNetworkListeners();
    this.removeVisibilityListener();
    this.eventReceived$.complete();
  }

  private initializeAuthSubscription() {
    logger.debug('Initializing auth subscription.');
    this.authService.authenticatedUser$
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        logger.info('Auth state changed.', {
          hasUser: Boolean(user),
          hasRealtimeToken: Boolean(user?.realtimeToken),
          tokenChanged: user?.realtimeToken !== this.lastConnectedToken,
          currentConnectionState: this.connectionState,
        });

        if (user && user.realtimeToken && this.lastConnectedToken !== user.realtimeToken) {
          logger.info('New realtime token detected. Initiating connection.', {
            tokenPreview: user.realtimeToken.slice(0, 6) + '***' + user.realtimeToken.slice(-3),
          });
          this.lastConnectedToken = user.realtimeToken;
          void this.connect(user.realtimeToken);
          return;
        }

        if (!user && this.lastConnectedToken) {
          logger.warn('User logged out. Disconnecting WebSocket.', {
            hadToken: Boolean(this.lastConnectedToken),
          });
          this.disconnect();
          this.lastConnectedToken = null;
          return;
        }

        if (!user?.realtimeToken) {
          logger.warn('User missing realtime token. Disconnecting WebSocket.', {
            hasUser: Boolean(user),
          });
          this.disconnect();
        }
      });
  }

  private initializeConnection() {
    logger.debug('Initializing connection.', {
      isAuthenticated: this.authService.isAuthenticated,
    });

    if (this.authService.isAuthenticated) {
      const token = this.authService.getRealtimeToken();
      if (token) {
        logger.debug('User is authenticated. Starting connection.', {
          tokenPreview: token.slice(0, 6) + '***' + token.slice(-3),
        });
        void this.connect(token);
      } else {
        logger.warn('User authenticated but no realtime token available.');
      }
    } else {
      logger.debug('User not authenticated. Skipping initial connection.');
    }
  }

  private initializeNetworkListeners() {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', this.boundOnlineHandler);
    window.addEventListener('offline', this.boundOfflineHandler);
    logger.info('Network listeners initialized.');
  }

  private removeNetworkListeners() {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('online', this.boundOnlineHandler);
    window.removeEventListener('offline', this.boundOfflineHandler);
  }

  private handleOnline() {
    logger.info('Network status changed to ONLINE.', {
      previousRetryAttempts: this.retryAttempts,
      connectionState: this.connectionState,
      wsReadyState: this.ws?.readyState,
    });
    this.isNetworkOnline = true;
    this.retryAttempts = 0; // Reset since this is a new network state

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.debug('Connection not established. Attempting reconnection.');
      this.attemptReconnect();
    } else {
      logger.debug('Connection already established. No action needed.');
    }
  }

  private handleOffline() {
    logger.warn('Network status changed to OFFLINE.', {
      hadPendingReconnect: Boolean(this.reconnectTimeout),
      currentRetryAttempts: this.retryAttempts,
      connectionState: this.connectionState,
    });
    this.isNetworkOnline = false;

    // Clear any pending reconnection attempts
    if (this.reconnectTimeout) {
      logger.debug('Clearing pending reconnection timeout.');
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private initializeVisibilityListener() {
    if (typeof document === 'undefined') {
      return;
    }

    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    logger.info('Visibility listener initialized.');
  }

  private removeVisibilityListener() {
    if (typeof document === 'undefined') {
      return;
    }

    document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      logger.info('Tab visibility changed to VISIBLE.', {
        connectionState: this.connectionState,
        wsReadyState: this.ws?.readyState,
        retryAttempts: this.retryAttempts,
        subscriptionCount: this.subscriptions.size,
      });

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        logger.info('Connection not established. Resetting retries and attempting reconnection.');
        this.retryAttempts = 0; // Reset on visibility change
        this.attemptReconnect();
      } else {
        logger.debug('Connection already active. No action needed.');
      }
    } else {
      logger.debug('Tab visibility changed to HIDDEN.', {
        connectionState: this.connectionState,
      });
    }
  }

  public async connect(token: string) {
    logger.debug('Connect method called.', {
      hasExistingConnection: Boolean(this.ws),
      currentState: this.connectionState,
    });

    if (!(await this.featureFlagService.isFeatureFlagEnabled(FeatureFlagsEnum.InfraRealtime))) {
      logger.warn('Feature flag InfraRealtime is disabled. Aborting connection.', {
        featureFlag: FeatureFlagsEnum.InfraRealtime,
      });
      return;
    }

    if (this.ws) {
      logger.warn('WebSocket connection already exists. Skipping connect.', {
        readyState: this.ws.readyState,
        readyStateLabel: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.ws.readyState],
      });
      return;
    }

    logger.info('Initiating WebSocket connection.', {
      url: environment.ISENGARD_REALTIME_URL,
      tokenPreview: token.slice(0, 6) + '***' + token.slice(-6),
      retryAttempts: this.retryAttempts,
    });

    this.connectionState = 'connecting';
    this.ws = new WebSocket(environment.ISENGARD_REALTIME_URL);

    this.ws.onopen = this.onConnect.bind(this);
    this.ws.onmessage = this.onMessage.bind(this);
    this.ws.onerror = this.onError.bind(this);
    this.ws.onclose = this.onClose.bind(this);
  }

  public disconnect() {
    logger.info('Disconnect method called.', {
      hasConnection: Boolean(this.ws),
      currentState: this.connectionState,
      wsReadyState: this.ws?.readyState,
      subscriptionCount: this.subscriptions.size,
    });

    if (this.ws) {
      logger.debug('Closing WebSocket connection.');
      this.ws.close();
    } else {
      logger.debug('No active WebSocket to close.');
    }

    if (this.interval) {
      logger.debug('Clearing heartbeat interval.');
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  private onConnect() {
    logger.info('WebSocket connection ESTABLISHED.', {
      previousState: this.connectionState,
      retryAttempts: this.retryAttempts,
      subscriptionCount: this.subscriptions.size,
      queueSize: this.queue.length,
    });
    this.connectionState = 'connected';
    this.retryAttempts = 0; // Reset retry attempts on successful connection
    this.debugConnectionDetails.push({
      connectedAt: new Date(),
      firstMessageAt: null,
      disconnectedAt: null,
      lastMessageAt: null,
    });
  }

  private onMessage(event: MessageEvent) {
    try {
      const rawMessage = event.data as string;
      eventLogger.info('Received message from server:', rawMessage);

      const lastConnection = this.debugConnectionDetails[this.debugConnectionDetails.length - 1];
      if (!lastConnection.firstMessageAt) {
        lastConnection.firstMessageAt = new Date();
      }
      if (lastConnection) {
        lastConnection.lastMessageAt = new Date();
      }

      const data = safeJsonParse<{ op: RealtimeEventType; data: any }>(rawMessage);
      if (!data || !data.op || !data.data) {
        eventLogger.warn('Invalid message format received:', rawMessage);
        return;
      }

      this.debugMessages.push(data);
      this.manageMemory();

      this.handleLifecycleEvents(data.op, data);

      const eventType = data.op as RealtimeEventType;
      eventLogger.info('Event type received:', eventType);
      const eventInstance = new REALTIME_EVENT_MAP[eventType](data.data);

      const hasFeatureFlag = this.featureFlagService.isFeatureFlagEnabled(
        FeatureFlagsEnum.InfraRealtime,
      );
      if (!hasFeatureFlag) {
        logger.warn('Feature flag InfraRealtime is not enabled. Ignoring event:', eventType);
        return;
      }

      this.eventReceived$.next({
        op: eventType,
        data: eventInstance,
      });

      eventLogger.info('Event processed and emitted:', eventType, eventInstance);
    } catch (error) {
      eventLogger.error('Error parsing message:', error);
    }
  }

  private onError(error: Event) {
    logger.error('WebSocket ERROR occurred.', {
      error,
      connectionState: this.connectionState,
      wsReadyState: this.ws?.readyState,
      retryAttempts: this.retryAttempts,
    });
    this.onClose();
  }

  private onClose() {
    logger.warn('WebSocket connection CLOSED.', {
      previousState: this.connectionState,
      retryAttempts: this.retryAttempts,
      subscriptionCount: this.subscriptions.size,
      queueSize: this.queue.length,
    });
    this.connectionState = 'disconnected';

    if (this.interval) {
      logger.debug('Clearing heartbeat interval on close.');
      clearInterval(this.interval);
      this.interval = null;
    }

    if (this.ws) {
      logger.debug('Nullifying WebSocket instance.');
      this.ws = null;
    }

    const lastConnection = this.debugConnectionDetails[this.debugConnectionDetails.length - 1];
    if (lastConnection) {
      lastConnection.disconnectedAt = new Date();
      const duration = lastConnection.connectedAt
        ? new Date().getTime() - lastConnection.connectedAt.getTime()
        : 0;
      logger.debug('Connection duration recorded.', {
        durationMs: duration,
        durationSec: Math.round(duration / 1000),
      });
    }

    // Attempt to reconnect if max retry attempts not reached
    logger.debug('Triggering reconnection attempt.');
    this.attemptReconnect();
  }

  private attemptReconnect() {
    logger.debug('Reconnection attempt initiated.', {
      isAuthenticated: this.authService.isAuthenticated,
      retryAttempts: this.retryAttempts,
      maxRetryAttempts: this.maxRetryAttempts,
      isNetworkOnline: this.isNetworkOnline,
      environment: environment.ENVIRONMENT,
    });

    if (!this.authService.isAuthenticated) {
      logger.warn('Reconnection ABORTED: User not authenticated.');
      return;
    }

    if (this.retryAttempts >= this.maxRetryAttempts) {
      logger.warn(
        `Reconnection ABORTED: Max retry attempts (${this.maxRetryAttempts}) reached. ` +
          'Will retry on network state change or visibility change.',
        {
          attemptsUsed: this.retryAttempts,
          maxAttempts: this.maxRetryAttempts,
        },
      );
      return;
    }

    if (!this.isNetworkOnline) {
      logger.warn('Reconnection ABORTED: Network is offline.', {
        isNetworkOnline: this.isNetworkOnline,
      });
      return;
    }

    if (environment.ENVIRONMENT === 'development') {
      logger.warn('Reconnection DISABLED in development environment.');
      return;
    }

    // Clear any existing reconnection timeout
    if (this.reconnectTimeout) {
      logger.debug('Clearing existing reconnection timeout.');
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.retryAttempts++;

    const backoffTime = this.calculateBackoffTime();
    logger.info(
      `Scheduling reconnection attempt ${this.retryAttempts}/${this.maxRetryAttempts} in ${Math.round(backoffTime)}ms.`,
      {
        attempt: this.retryAttempts,
        maxAttempts: this.maxRetryAttempts,
        delayMs: Math.round(backoffTime),
        delaySec: Math.round(backoffTime / 1000),
      },
    );

    this.reconnectTimeout = setTimeout(() => {
      logger.debug('Reconnection timeout triggered. Executing reconnection.');
      this.reconnectTimeout = null;

      const token = this.authService.getRealtimeToken();
      if (token) {
        logger.debug('Token available. Reconnecting and resubscribing.', {
          subscriptionCount: this.subscriptions.size,
        });
        void this.connect(token);
        for (const subs of this.subscriptions) {
          logger.debug('Resubscribing to:', { subscription: subs });
          this.subscribe(subs as FluidPrefixType);
        }
      } else {
        logger.error('Reconnection FAILED: No token available.');
      }
    }, backoffTime);
  }

  /**
   * Calculates exponential backoff time with jitter.
   * Starts at 1 second and caps at 30 seconds.
   *
   * Formula: min(baseDelay * 2^retryAttempts, maxDelay) + jitter
   */
  private calculateBackoffTime(): number {
    const baseDelay = 1000; // Start with 1 second
    const maxDelay = 30000; // Cap at 30 seconds

    const exponentialDelay = Math.min(baseDelay * Math.pow(2, this.retryAttempts - 1), maxDelay);

    // Add jitter (±25% of the delay)
    const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);

    return exponentialDelay + jitter;
  }

  private sendHeartbeat() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const evtId = this.generateEventId();
      logger.debug('Sending heartbeat.', { evt_id: evtId });
      this.ws.send(
        new ClientHeartbeatEvent({
          evt_id: evtId,
          token: this.authService.getRealtimeToken() ?? '',
        }).toJSON(),
      );
    } else {
      logger.warn('Cannot send heartbeat: WebSocket not open.', {
        wsExists: Boolean(this.ws),
        readyState: this.ws?.readyState,
      });
    }
  }

  public subscribe(fluid: FluidPrefixType) {
    logger.debug('Subscribe method called.', { fluid });

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.warn('WebSocket not open. Queueing subscription.', {
        fluid,
        queueSize: this.queue.length,
        wsReadyState: this.ws?.readyState,
      });
      this.queue.push(() => this.subscribe(fluid));
      return;
    }

    const evtId = this.generateEventId();
    logger.info('Sending subscription request.', {
      fluid,
      evt_id: evtId,
    });

    this.ws.send(
      new ClientSubscribeEvent({
        evt_id: evtId,
        add: [fluid],
      }).toJSON(),
    );

    this.subscribeEvents[evtId] = {
      id: evtId,
      type: 'add',
      fluids: [fluid],
    };
  }

  public unsubscribe(fluid: FluidPrefixType) {
    logger.debug('Unsubscribe method called.', { fluid });

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.warn('WebSocket not open. Queueing unsubscription.', {
        fluid,
        queueSize: this.queue.length,
        wsReadyState: this.ws?.readyState,
      });
      this.queue.push(() => this.unsubscribe(fluid));
      return;
    }

    const evtId = this.generateEventId();
    logger.info('Sending unsubscription request.', {
      fluid,
      evt_id: evtId,
    });

    this.ws.send(
      new ClientUnsubscribeEvent({
        evt_id: evtId,
        remove: [fluid],
      }).toJSON(),
    );

    this.subscribeEvents[evtId] = {
      id: evtId,
      type: 'remove',
      fluids: [fluid],
    };
  }

  public generateDebugReport() {
    return {
      debugMessages: this.debugMessages,
      retryAttempts: this.retryAttempts,
      maxRetryAttempts: this.maxRetryAttempts,
      debugConnectionDetails: this.debugConnectionDetails,
      connectionState: this.connectionState,
      subscriptions: Array.from(this.subscriptions),
    };
  }

  private generateEventId() {
    // Generate a random 14-digit number as a string
    return String(
      Math.floor(Math.random() * (99999999999999 - 10000000000000 + 1)) + 10000000000000,
    );
  }

  private manageMemory() {
    const maxMessages = 100;
    const maxConnections = 10;

    if (this.debugMessages.length > maxMessages) {
      this.debugMessages.splice(0, this.debugMessages.length - maxMessages);
    }

    if (this.debugConnectionDetails.length > maxConnections) {
      this.debugConnectionDetails.splice(0, this.debugConnectionDetails.length - maxConnections);
    }
  }

  private async handleLifecycleEvents(op: RealtimeEventType, receivedEvent: { data: any }) {
    eventLogger.info('Event received.', {
      op,
      data: receivedEvent.data,
    });

    if (op === 'SystemConnect') {
      const evtId = this.generateEventId();
      logger.info('SystemConnect event received. Sending ClientAuthEvent.', {
        evt_id: evtId,
      });
      this.ws?.send(
        new ClientAuthEvent({
          evt_id: evtId,
          token: this.authService.getRealtimeToken() ?? '',
        }).toJSON(),
      );
    }

    if (op === 'SystemHello') {
      const event = new SystemHelloEvent(receivedEvent.data);
      logger.info('SystemHello event received. Initializing subscriptions.', {
        serverSubscriptions: event.payload?.init_subs?.length ?? 0,
        heartbeatInterval: receivedEvent.data.heartbeat_interval,
      });

      this.subscriptions = new Set(event.payload?.init_subs);
      this.helloId = this.generateEventId();

      logger.info('Sending ClientHello event.', {
        evt_id: this.helloId,
        subscriptionCount: this.subscriptions.size,
        clientVersion: environment.GRAPHQL_CLIENT_VERSION,
      });

      this.ws?.send(
        new ClientHelloEvent({
          evt_id: this.helloId,
          token: this.authService.getRealtimeToken() ?? '',
          client_version: environment.GRAPHQL_CLIENT_VERSION,
          init_subs: Array.from(this.subscriptions),
        }).toJSON(),
      );

      logger.debug('Setting up heartbeat interval.', {
        intervalMs: receivedEvent.data.heartbeat_interval,
      });
      this.interval = setInterval(() => {
        this.sendHeartbeat();
      }, receivedEvent.data.heartbeat_interval);
    }

    if (op === 'SystemHeartbeat') {
      logger.debug('SystemHeartbeat event received. Sending heartbeat response.');
      this.sendHeartbeat();
    }

    if (op === RealtimeEventType.SystemAcknowledge) {
      const event = new SystemAckEvent(receivedEvent.data);
      const evtId = event.data.ack_id;

      logger.debug('SystemAcknowledge event received.', {
        ack_id: evtId,
      });

      if (!evtId) {
        logger.warn('SystemAckEvent missing ack_id. Ignoring.');
        return;
      }

      if (this.helloId === evtId) {
        logger.info('ClientHello acknowledged. Flushing message queue.', {
          queueSize: this.queue.length,
        });
        for (const item of this.queue) {
          logger.debug('Processing queued operation.');
          item(this);
        }
        this.queue = [];
        logger.debug('Queue flushed.');
        return;
      }

      const subscriptionEvent = this.subscribeEvents[evtId];
      if (subscriptionEvent) {
        switch (subscriptionEvent.type) {
          case 'add':
            this.subscriptions.add(subscriptionEvent.fluids[0]);
            logger.info('Subscription CONFIRMED.', {
              fluid: subscriptionEvent.fluids[0],
              totalSubscriptions: this.subscriptions.size,
            });
            break;
          case 'remove':
            this.subscriptions.delete(subscriptionEvent.fluids[0]);
            logger.info('Unsubscription CONFIRMED.', {
              fluid: subscriptionEvent.fluids[0],
              totalSubscriptions: this.subscriptions.size,
            });
            break;
        }

        Reflect.deleteProperty(this.subscribeEvents, evtId); // Clean up after processing
      } else {
        logger.debug('Acknowledgment for unknown event.', { ack_id: evtId });
      }
    }

    if (op === 'SystemError') {
      const event = new SystemErrorEvent(receivedEvent.data);
      const evtId = event.payload?.ack_id;

      logger.error('SystemError event received.', {
        errorData: receivedEvent.data,
        ack_id: evtId,
      });

      if (!evtId) {
        logger.warn('SystemErrorEvent missing ack_id.');
        return;
      }

      const subscriptionEvent = this.subscribeEvents[evtId];
      if (subscriptionEvent) {
        logger.error('Subscription/Unsubscription FAILED.', {
          type: subscriptionEvent.type,
          fluid: subscriptionEvent.fluids[0],
          evt_id: evtId,
        });
        Reflect.deleteProperty(this.subscribeEvents, evtId); // Clean up after processing
      } else {
        logger.error('Error for unknown event.', { ack_id: evtId });
      }
    }
  }
}
